import '../../scss/ExamDetail.scss';
import { Button } from 'primereact/button';
import React, { useState, useRef, useEffect, useCallback } from "react";
import { ThiTrucTuyenService } from 'services/ThiTrucTuyenService';
import { ProgressSpinner } from 'primereact/progressspinner';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetExamProgress } from 'utils/examProgress';
import { Toast } from 'primereact/toast';

// ============================================================
// CEFR Scoring — mirror chính xác logic backend (CaThi.php + config.php)
// Dữ liệu `tong_diem` trong DB = điểm hiển thị (giống admin)
// ============================================================

const CEFR_THRESHOLDS = {
    NGUPHAP: { A0_min: 8, A1: 15, A2: 23, B1: 33, B2: 41 },
    NGHE: { A0_min: 8, A1: 15, A2: 23, B1: 33, B2: 41 },
    DOC: { A0_min: 8, A1: 15, A2: 25, B1: 37, B2: 45 },
    VIET: { A0_min: 6, A1: 17, A2: 25, B1: 39, B2: 47 },
    NOI: { A0_min: 4, A1: 15, A2: 25, B1: 40, B2: 47 },
};

function getCEFR(skill, score) {
    score = parseFloat(score || 0);
    const t = CEFR_THRESHOLDS[skill];
    if (!t) return '-';
    if (score < t.A0_min) return 'A0';
    if (score <= t.A1) return 'A1';
    if (score <= t.A2) return 'A2';
    if (score <= t.B1) return 'B1';
    if (score <= t.B2) return 'B2';
    return 'C1';
}

function cefrToNum(cefr) {
    const map = { A1: 1, A2: 2, B1: 3, B2: 4, C1: 5 };
    return map[cefr] !== undefined ? map[cefr] : 0;
}

function numToCEFR(num) {
    const map = { 1: 'A1', 2: 'A2', 3: 'B1', 4: 'B2', 5: 'C1' };
    return map[num] || '-';
}

const CEFR_COLORS = {
    A0: { bg: '#f3f4f6', color: '#6b7280', border: '#d1d5db' },
    A1: { bg: '#e0f2fe', color: '#0369a1', border: '#7dd3fc' },
    A2: { bg: '#dbeafe', color: '#1d4ed8', border: '#93c5fd' },
    B1: { bg: '#dcfce7', color: '#15803d', border: '#86efac' },
    B2: { bg: '#fef3c7', color: '#a16207', border: '#fcd34d' },
    C1: { bg: '#fee2e2', color: '#b91c1c', border: '#fca5a5' },
};

const SKILL_META = {
    NGUPHAP: { label: 'Ngữ pháp & Từ vựng', icon: '📝', englabel: 'Grammar & Vocabulary' },
    NGHE: { label: 'Nghe', icon: '🎧', englabel: 'Listening' },
    DOC: { label: 'Đọc', icon: '📖', englabel: 'Reading' },
    NOI: { label: 'Nói', icon: '🎙️', englabel: 'Speaking' },
    VIET: { label: 'Viết', icon: '✍️', englabel: 'Writing' },
};

const AI_SKILLS = ['NOI', 'VIET'];
const POLL_INTERVAL = 10000;

export default function ExamResult() {
    var IdDeThi = localStorage.getItem('IdDeThi');
    const userId = localStorage.getItem('User_Id');
    const navigate = useNavigate();
    const location = useLocation();
    const { isFinalResult } = location.state || {};
    const [loading, setLoading] = useState(false);
    const [scores, setScores] = useState([]);
    const [attemptCode, setAttemptCode] = useState(localStorage.getItem('exam_attempt_code'));
    const [polling, setPolling] = useState(false);
    const toast = useRef(null);
    const pollTimerRef = useRef(null);
    const pollCountRef = useRef(0);

    const onExit = () => {
        stopPolling();
        resetExamProgress();
        localStorage.removeItem('exam_attempt_code');
        navigate('/');
    };

    useEffect(() => {
        window.history.pushState(null, '', window.location.href);
        const handlePopState = () => {
            window.history.pushState(null, '', window.location.href);
            stopPolling();
            resetExamProgress();
            window.location.replace('/');
        };
        window.addEventListener('popstate', handlePopState);
        return () => {
            window.removeEventListener('popstate', handlePopState);
            stopPolling();
        };
    }, []);

    const stopPolling = useCallback(() => {
        if (pollTimerRef.current) {
            clearInterval(pollTimerRef.current);
            pollTimerRef.current = null;
        }
        setPolling(false);
    }, []);

    const hasAiPending = useCallback((scoreData) => {
        for (const skill of AI_SKILLS) {
            const row = scoreData.find(s => s.type_exam === skill);
            if (row && parseInt(row.ai_graded || 0) === 0) return true;
        }
        return false;
    }, []);

    const fetchScores = useCallback(async (isInitial = false) => {
        try {
            const result = await ThiTrucTuyenService.getoverviewexam({
                user_id: userId,
                isthitructuyen: 1,
                IdDeThi: IdDeThi
            });
            const rawScores = result?.data?.scores || [];
            setScores(rawScores);

            if (rawScores.length) {
                // Sắp xếp để lấy attempt_code từ lần thi mới nhất nếu có nhiều lần (từ bất kỳ dòng nào)
                const sortedScores = [...rawScores].sort((a, b) =>
                    new Date(b.time_bat_dau_thi || 0) - new Date(a.time_bat_dau_thi || 0)
                );
                // Ưu tiên các lượt thi của Phase 2 (thường có attempt_code giống nhau cho 4 kỹ năng)
                const codeFromApi = sortedScores.find(s => s.attempt_code && s.type_exam !== 'NOI')?.attempt_code
                    || sortedScores.find(s => s.attempt_code)?.attempt_code
                    || null;

                const finalCode = codeFromApi || attemptCode;
                if (finalCode && finalCode !== 'null') {
                    console.log('[ExamResult] Setting attemptCode:', finalCode);
                    setAttemptCode(finalCode);
                }

                if (isInitial && isFinalResult) {
                    const latestScore = rawScores.find(s => s.time_ket_thuc_thi) || rawScores[0];
                    const examDate = latestScore?.time_ket_thuc_thi || new Date().toISOString();
                    const totalQ = rawScores.reduce((s, r) => s + parseInt(r.tongsocau || 0), 0);
                    const totalCorrect = rawScores.reduce((s, r) => s + parseInt(r.socaudung || 0), 0);

                    saveExamResultToHistory({
                        attempt_code: finalCode,
                        examId: IdDeThi,
                        examName: rawScores[0]?.dethitructuyen_name || 'Đề thi',
                        date: examDate,
                        scores: rawScores,
                        totalQuestions: totalQ,
                        totalCorrect
                    });
                }
            }

            return rawScores;
        } catch (err) {
            console.error('[ExamResult] fetchScores error:', err);
            return [];
        }
    }, [userId, IdDeThi, isFinalResult]);

    const startPolling = useCallback((currentScores) => {
        if (!hasAiPending(currentScores)) {
            setPolling(false);
            return;
        }

        setPolling(true);
        pollCountRef.current = 0;

        pollTimerRef.current = setInterval(async () => {
            pollCountRef.current += 1;

            if (pollCountRef.current >= 60) {
                stopPolling();
                return;
            }

            try {
                const newScores = await fetchScores(false);
                if (!hasAiPending(newScores)) {
                    stopPolling();
                    if (toast.current) {
                        toast.current.show({
                            severity: 'success',
                            summary: 'Cập nhật điểm',
                            detail: 'Điểm Nói / Viết đã được AI chấm xong!',
                            life: 5000,
                        });
                    }
                }
            } catch (err) {
                console.error('[ExamResult] polling error:', err);
            }
        }, POLL_INTERVAL);
    }, [hasAiPending, fetchScores, stopPolling]);

    useEffect(() => {
        const init = async () => {
            setLoading(true);
            const data = await fetchScores(true);
            setLoading(false);

            if (isFinalResult) {
                setTimeout(() => resetExamProgress(), 1000);
            }
            startPolling(data);
        };
        init();

        return () => stopPolling();
    }, []);

    const saveExamResultToHistory = (examResult) => {
        try {
            const historyKey = `exam_history_${userId}`;
            const existingHistory = JSON.parse(localStorage.getItem(historyKey) || '[]');
            const attemptCode = examResult.attempt_code;
            let existingIndex = -1;
            if (attemptCode) {
                existingIndex = existingHistory.findIndex(item => item.attempt_code === attemptCode);
            } else {
                const examDate = new Date(examResult.date);
                existingIndex = existingHistory.findIndex(item => {
                    if (item.examId === examResult.examId) {
                        return Math.abs(examDate - new Date(item.date)) < 60000;
                    }
                    return false;
                });
            }
            if (existingIndex >= 0) {
                existingHistory[existingIndex] = examResult;
            } else {
                existingHistory.unshift(examResult);
            }
            localStorage.setItem(historyKey, JSON.stringify(existingHistory.slice(0, 50)));
        } catch (error) {
            console.error('Error saving exam result:', error);
        }
    };

    const computeSkillData = () => {
        const skillOrder = ['NGUPHAP', 'NGHE', 'DOC', 'NOI', 'VIET'];

        const scoreMap = {};
        scores.forEach(row => {
            scoreMap[row.type_exam] = row;
        });

        const nguphapRow = scoreMap['NGUPHAP'];
        const nguphapScore = nguphapRow ? parseFloat(nguphapRow.tong_diem || 0) : 0;

        const hasBonus = nguphapScore >= 32;

        const applyBonus = (rawVal) => {
            if (rawVal === null) return null;
            const v = parseFloat(rawVal);
            return hasBonus ? Math.min(50, Math.round(v * 1.05)) : v;
        };

        // Lấy điểm đã quy đổi sang thang 50 (quy_diem).
        // Ưu tiên quy_diem đã lưu DB. Fallback: tự tính từ tong_diem (dữ liệu cũ).
        const getScaleScore = (key) => {
            const row = scoreMap[key];
            if (!row) return null;

            const stored = row.quy_diem !== undefined ? parseFloat(row.quy_diem) : 0;
            const raw = row.tong_diem !== undefined ? parseFloat(row.tong_diem) : null;

            // Ưu tiên dùng quy_diem đã lưu (> 0)
            if (stored > 0) return stored;

            // Fallback: tính quy_diem từ tong_diem (dữ liệu cũ chưa có quy_diem)
            if (raw === null) return null;
            if (key === 'NGHE') return Math.min(50, raw * 2);        // thô/25 × 50 = thô × 2
            if (key === 'DOC') return Math.min(50, raw / 25 * 50);  // thô/25 × 50
            // VIET/NOI: AI trả thang 0-50, tong_diem đã là quy_diem
            return raw;
        };

        const displayScores = {
            // NGUPHAP: để nguyên tong_diem (không quy sang thang 50, không quy CEFR)
            NGUPHAP: nguphapRow ? parseFloat(nguphapRow.tong_diem || 0) : null,
            // 4 kỹ năng còn lại: dùng quy_diem (thang 50) + bonus
            NGHE: applyBonus(getScaleScore('NGHE')),
            DOC: applyBonus(getScaleScore('DOC')),
            NOI: applyBonus(getScaleScore('NOI')),
            VIET: applyBonus(getScaleScore('VIET')),
        };

        const cefrResults = {};
        for (const key of skillOrder) {
            cefrResults[key] = displayScores[key] !== null ? getCEFR(key, displayScores[key]) : '-';
        }

        let tongDiem = 0;
        ['NGHE', 'DOC', 'NOI', 'VIET'].forEach(k => {
            if (displayScores[k] !== null) tongDiem += displayScores[k];
        });

        let cefrOverall = '-';
        const fourCefrs = ['NGHE', 'DOC', 'NOI', 'VIET'].map(k => cefrResults[k]);
        const hasA0 = fourCefrs.some(c => c === 'A0');
        const allDone = fourCefrs.every(c => c !== '-');
        if (allDone && !hasA0) {
            const nums = fourCefrs.map(cefrToNum);
            const avg = nums.reduce((a, b) => a + b, 0) / 4;
            cefrOverall = numToCEFR(Math.round(avg));
        }

        const skills = skillOrder.map(key => {
            const row = scoreMap[key];
            const isAiSkill = AI_SKILLS.includes(key);
            const aiGraded = row ? parseInt(row.ai_graded || 0) : 0;
            const waitingAi = isAiSkill && row && aiGraded === 0;

            return {
                key,
                meta: SKILL_META[key],
                displayScore: displayScores[key],
                rawScore: row ? row.tong_diem : null,
                cefr: cefrResults[key],
                hasTaken: !!row,
                hasBonus: key !== 'NGUPHAP' && hasBonus && displayScores[key] !== null,
                waitingAi,
                aiGraded: aiGraded === 1,
            };
        });

        return { skills, cefrOverall, tongDiem, hasBonus };
    };

    const computed = scores.length > 0 ? computeSkillData() : { skills: [], cefrOverall: '-', tongDiem: 0, hasBonus: false };
    const { skills, cefrOverall, tongDiem, hasBonus } = computed;
    const hasPendingAiSkills = skills.some(s => s.waitingAi);

    const CefrBadge = ({ level, size = 'normal' }) => {
        if (!level || level === '-') {
            return <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: size === 'small' ? 12 : 14 }}>–</span>;
        }
        const colors = CEFR_COLORS[level] || CEFR_COLORS.A0;
        const isSmall = size === 'small';
        return (
            <span style={{
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                background: colors.bg, color: colors.color, border: `2px solid ${colors.border}`,
                borderRadius: isSmall ? 6 : 10,
                padding: isSmall ? '2px 10px' : '6px 18px',
                fontWeight: 800, fontSize: isSmall ? 13 : 18,
                letterSpacing: 1.5, minWidth: isSmall ? 42 : 56,
                fontFamily: "'Segoe UI', 'Roboto', monospace",
            }}>
                {level}
            </span>
        );
    };

    const fmtScore = (val) => {
        if (val === null || val === undefined) return '–';
        return parseFloat(val).toFixed(1);
    };

    const SkillCard = ({ skill }) => {
        const { meta, displayScore, hasTaken, waitingAi, key } = skill;

        // Ưu tiên lấy bản ghi is_completed=1 khi có nhiều bản ghi cùng type_exam
        const row = scores.filter(s => s.type_exam === key).find(s => parseInt(s.is_completed) === 1)
                 || scores.find(s => s.type_exam === key);

        // Lấy trực tiếp từ DB, không tự tính lại
        const tongDiem = row ? parseFloat(row.tong_diem || 0) : 0;  // điểm thô

        return (
            <div style={{
                background: waitingAi ? '#fffbeb' : '#fff',
                borderRadius: 20,
                padding: '24px',
                boxShadow: '0 4px 15px rgba(0,0,0,0.03)',
                border: waitingAi ? '1px solid #fde68a' : '1px solid #f1f5f9',
                display: 'flex', flexDirection: 'column', gap: 16,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: 16,
                        background: waitingAi
                            ? 'linear-gradient(135deg, #fef3c71a, #fbbf241a)'
                            : 'linear-gradient(135deg, #6366f11a, #a855f71a)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 28, flexShrink: 0,
                    }}>
                        {meta.icon}
                    </div>
                    <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: 800, fontSize: 17, color: '#1e293b' }}>{meta.label}</div>
                        <div style={{ fontSize: 12, color: '#9ca3af' }}>{meta.englabel}</div>
                        {waitingAi && (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, fontSize: 12, color: '#d97706' }}>
                                <span className="ai-pulse" style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }}></span>
                                Đang chờ AI chấm điểm...
                            </div>
                        )}
                    </div>
                    <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 28, fontWeight: 900, color: '#0f172a', fontFamily: "'Segoe UI', 'Roboto', sans-serif" }}>
                            {hasTaken && !waitingAi ? fmtScore(displayScore) : (waitingAi ? (
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 2s linear infinite' }}>
                                    <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93"
                                        stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
                                </svg>
                            ) : '–')}
                        </div>
                        <div style={{ fontSize: 10, color: '#94a3b8', fontWeight: 700, letterSpacing: 0.5 }}>
                            {key === 'NGUPHAP' ? 'ĐIỂM THỰC TẾ' : 'ĐIỂM QUY ĐỔI'}
                        </div>
                    </div>
                </div>

                {key === 'NGUPHAP' && tongDiem >= 32 && (
                    <div style={{
                        background: '#ecfdf5',
                        padding: '12px 16px',
                        borderRadius: 12,
                        border: '1px solid #10b9814d'
                    }}>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 6 }}>
                            <i className="pi pi-exclamation-circle" style={{ color: '#059669', fontSize: 14 }}></i>
                            <span style={{ fontWeight: 800, fontSize: 12, color: '#065f46', textTransform: 'uppercase' }}>Lưu ý quan trọng:</span>
                        </div>
                        <div style={{ fontSize: 12, color: '#065f46', lineHeight: 1.5, fontWeight: 500 }}>
                            <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700 }}>
                                ✅ Ngữ pháp ≥ 32 → Đã áp dụng thưởng hệ số 1.05 cho 4 kỹ năng còn lại
                            </div>
                            <div style={{ marginTop: 8, fontSize: 13, fontWeight: 700 }}>
                                Kết quả: ✅ Đã đủ điều kiện và áp dụng thưởng hệ số 1.05
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', height: '100vh', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <ProgressSpinner />
            </div>
        );
    }

    return (
        <>
            <Toast ref={toast} />
            <style>{`
                @keyframes fadeInUp {
                    from { opacity: 0; transform: translateY(20px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                @keyframes scaleIn {
                    from { opacity: 0; transform: scale(0.9); }
                    to { opacity: 1; transform: scale(1); }
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
                @keyframes pulse {
                    0%, 100% { opacity: 1; }
                    50% { opacity: 0.4; }
                }
                .ai-pulse {
                    animation: pulse 1.5s ease-in-out infinite;
                }
                .result-container {
                    min-height: 100vh;
                    background: #f8f9fa;
                    padding: 30px 20px 60px;
                    font-family: 'Segoe UI', 'Roboto', sans-serif;
                }
                .result-wrapper {
                    max-width: 680px;
                    margin: 0 auto;
                }
                .congrats-banner {
                    background: #ffffff;
                    border-radius: 20px;
                    padding: 40px 28px;
                    text-align: center;
                    position: relative;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
                    border: 1px solid #eaeaea;
                    animation: fadeInUp 0.6s ease-out;
                }
                .overall-ring {
                    width: 96px; height: 96px;
                    border-radius: 50%;
                    background: #f3f4f6;
                    display: flex; align-items: center; justify-content: center;
                    margin: 0 auto 16px;
                    animation: scaleIn 0.8s ease-out;
                }
                .overall-inner {
                    width: 72px; height: 72px;
                    border-radius: 50%;
                    background: white;
                    display: flex; align-items: center; justify-content: center;
                    flex-direction: column;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
                }
                .skills-grid {
                    display: grid;
                    grid-template-columns: 1fr;
                    gap: 12px;
                    margin-top: 18px;
                    animation: fadeInUp 0.6s ease-out 0.2s both;
                }
                .score-summary-card {
                    background: #fff;
                    border-radius: 20px;
                    padding: 24px;
                    margin-top: 16px;
                    box-shadow: 0 4px 20px rgba(0,0,0,0.04);
                    border: 1px solid #eaeaea;
                    animation: fadeInUp 0.6s ease-out 0.4s both;
                }
                .exit-btn-area {
                    text-align: center;
                    margin-top: 24px;
                    animation: fadeInUp 0.6s ease-out 0.6s both;
                }
                @media (max-width: 480px) {
                    .congrats-banner { padding: 30px 16px; }
                    .result-container { padding: 16px 10px 40px; }
                }
            `}</style>

            <div className="result-container">
                <div className="result-wrapper">

                    <div className="congrats-banner">
                        <div className="overall-ring">
                            <div className="overall-inner">
                                {cefrOverall && cefrOverall !== '-' ? (
                                    <>
                                        <div style={{ fontSize: 10, color: '#6b7280', fontWeight: 600, lineHeight: 1, textTransform: 'uppercase' }}>Overall</div>
                                        <div style={{
                                            fontSize: 22, fontWeight: 900,
                                            color: CEFR_COLORS[cefrOverall]?.color || '#111827',
                                            lineHeight: 1.2, letterSpacing: 1,
                                            fontFamily: "'Segoe UI', monospace"
                                        }}>
                                            {cefrOverall}
                                        </div>
                                    </>
                                ) : (
                                    <svg width="34" height="34" viewBox="0 0 24 24" fill="none">
                                        <path d="M9 12L11 14L15 10M21 12C21 16.97 16.97 21 12 21C7.03 21 3 16.97 3 12C3 7.03 7.03 3 12 3C16.97 3 21 7.03 21 12Z"
                                            stroke="#10b981" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </div>
                        </div>

                        <h1 style={{
                            fontSize: 24, fontWeight: 800, color: '#111827', margin: '0 0 8px',
                            letterSpacing: '-0.5px'
                        }}>
                            Hoàn thành bài thi!
                        </h1>
                        <p style={{
                            fontSize: 15, color: '#6b7280', margin: 0,
                        }}>
                            Kết quả được cập nhật từ hệ thống
                        </p>

                        {skills.length > 0 && (
                            <div style={{
                                marginTop: 18,
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                background: '#f8f9fa',
                                border: '1px solid #eaeaea',
                                borderRadius: 12,
                                padding: '10px 24px',
                                flexWrap: 'wrap',
                                justifyContent: 'center'
                            }}>
                                <span style={{ color: '#6b7280', fontSize: 13, fontWeight: 600 }}>Tổng điểm 4 kỹ năng:</span>
                                <span style={{ color: '#111827', fontWeight: 800, fontSize: 20, fontFamily: 'monospace' }}>
                                    {tongDiem.toFixed(1)}
                                </span>
                                <span style={{ color: '#9ca3af', fontSize: 13 }}>/ 200</span>
                                {hasBonus && (
                                    <span style={{
                                        background: '#fef3c7',
                                        color: '#92400e',
                                        borderRadius: 6,
                                        padding: '2px 8px',
                                        fontSize: 11,
                                        fontWeight: 700,
                                        marginLeft: 4
                                    }}>
                                        ×1.05 bonus
                                    </span>
                                )}
                                {hasPendingAiSkills && (
                                    <span style={{ color: '#d97706', fontSize: 11, fontWeight: 700 }}>
                                        (Tạm tính, đang chờ AI chấm)
                                    </span>
                                )}
                            </div>
                        )}
                    </div>

                    {skills.length > 0 && (
                        <div className="score-summary-card">
                            <div style={{
                                fontWeight: 700, fontSize: 16, color: '#1e1b4b', marginBottom: 3,
                                display: 'flex', alignItems: 'center', gap: 8,
                            }}>
                                <span>📊</span>
                                <span>Kết quả theo kỹ năng</span>
                            </div>
                            <div style={{ fontSize: 12, color: '#9ca3af', marginBottom: 14 }}>
                                {hasBonus && <span style={{ color: '#a16207' }}> </span>}
                            </div>

                            <div className="skills-grid">
                                {skills.map(skill => (
                                    <SkillCard key={skill.key} skill={skill} />
                                ))}
                            </div>

                            {polling && (
                                <div style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    background: '#fffbeb', border: '1px solid #fde68a',
                                    borderRadius: 12, padding: '10px 16px',
                                    marginTop: 14, fontSize: 13, color: '#92400e',
                                }}>
                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 2s linear infinite', flexShrink: 0 }}>
                                        <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93"
                                            stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <span>Đang chờ AI chấm điểm <strong>Nói</strong> / <strong>Viết</strong>... Tự động cập nhật mỗi 10 giây</span>
                                </div>
                            )}

                            <div style={{
                                marginTop: 18, borderTop: '1px solid #f0f0f0', paddingTop: 18,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <div>
                                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e1b4b' }}>Overall CEFR</div>
                                    <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 2 }}>
                                        {cefrOverall !== '-'
                                            ? 'Trung bình 4 kỹ năng (Nghe, Đọc, Nói, Viết)'
                                            : polling
                                                ? 'Đang chờ AI chấm điểm...'
                                                : 'Cần đủ 4 kỹ năng, không có A0'}
                                    </div>
                                </div>
                                <div>
                                    {cefrOverall !== '-' ? (
                                        <CefrBadge level={cefrOverall} size="normal" />
                                    ) : (
                                        <span style={{
                                            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                            background: polling ? '#fef3c7' : '#f3f4f6',
                                            color: polling ? '#d97706' : '#9ca3af',
                                            border: polling ? '2px dashed #fcd34d' : '2px dashed #d1d5db',
                                            borderRadius: 10, padding: '6px 18px',
                                            fontWeight: 700, fontSize: 16, minWidth: 56,
                                        }}>
                                            {polling ? <svg width="18" height="18" viewBox="0 0 24 24" fill="none" style={{ animation: 'spin 2s linear infinite' }}>
                                                <path d="M12 2V6M12 18V22M4.93 4.93L7.76 7.76M16.24 16.24L19.07 19.07M2 12H6M18 12H22M4.93 19.07L7.76 16.24M16.24 7.76L19.07 4.93"
                                                    stroke="#d97706" strokeWidth="2" strokeLinecap="round" />
                                            </svg> : '–'}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="exit-btn-area" style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
                        <Button
                            style={{
                                background: '#ffffff',
                                color: '#111827',
                                border: '1px solid #d1d5db',
                                padding: '12px 32px',
                                fontSize: 16, fontWeight: 600,
                                borderRadius: 14,
                                cursor: 'pointer',
                                transition: 'all 0.2s ease',
                                fontFamily: "'Segoe UI', 'Roboto', sans-serif",
                            }}
                            onClick={() => {
                                if (attemptCode && attemptCode !== 'null') {
                                    navigate(`/review-exam/${attemptCode}`);
                                } else {
                                    alert("Không tìm thấy mã lượt thi (Attempt Code). Vui lòng thử lại sau hoặc liên hệ quản trị viên.");
                                }
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.background = '#f3f4f6';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.background = '#ffffff';
                            }}
                        >
                            Xem chi tiết bài thi
                        </Button>

                        <Button
                            style={{
                                background: 'linear-gradient(135deg, #667eea, #764ba2)',
                                border: 'none',
                                padding: '12px 32px',
                                fontSize: 16, fontWeight: 600,
                                borderRadius: 14,
                                boxShadow: '0 4px 14px rgba(102, 126, 234, 0.25)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                color: '#fff',
                                fontFamily: "'Segoe UI', 'Roboto', sans-serif",
                            }}
                            onClick={onExit}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 6px 20px rgba(102, 126, 234, 0.35)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 4px 14px rgba(102, 126, 234, 0.25)';
                            }}
                        >
                            Đăng xuất
                        </Button>
                    </div>
                </div>
            </div>
        </>
    );
}
